import type {
  PricingCategory,
  Price,
  EventDescription,
} from '~/composables/types/event'
import type { CategoryItem } from '~/components/common/PricingCategoryQuantityList.vue'
import { formatMoney } from '~/utils/currencyHelpers'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import { calculatePromotionDiscount } from '~/utils/pricingGetters'

export interface PriceBreakdownItem {
  title: string
  total_participants: number
  price_per_person: Price
  total_price: Price
  original_price_per_person?: Price
}

interface FormatCategoriesOptions {
  pricesByCategory?: PriceBreakdownItem[]
  sortCategories?: boolean
  staticPricingCategories?: PricingCategory[]
  categoryQuantities?: Record<string, number>
  loadingPricings?: boolean
  eventData?: EventDescription
}

function buildAgeRangeLabel(
  minAge: number,
  maxAge: number,
  t: (key: string, params?: Record<string, any>) => string
): string {
  // Youngest category uses translated "X years and younger" format
  if (minAge === 0) {
    return t('product.show.checkout.ageAndYounger', { age: maxAge })
  }
  // Seniors (maxAge >= 99) show only minimum age with "+"
  if (maxAge >= 99) {
    return `${minAge}+`
  }
  // Regular range shows both min and max
  return `${minAge}-${maxAge}`
}

function findPriceForCategory(
  categoryType: string,
  pricesByCategory?: PriceBreakdownItem[]
): Price | undefined {
  return pricesByCategory?.find((item) => item.title === categoryType)
    ?.price_per_person
}

function findOriginalPriceForCategory(
  categoryType: string,
  pricesByCategory?: PriceBreakdownItem[]
): Price | undefined {
  return pricesByCategory?.find((item) => item.title === categoryType)
    ?.original_price_per_person
}

function getStaticPriceAmount(
  categoryType: string,
  staticPricingCategories?: PricingCategory[]
): number | undefined {
  const retailPrice = staticPricingCategories?.find(
    (cat) => cat.ticket_category === categoryType
  )?.pricingScales?.data[0]?.price?.data?.retail_price

  if (!retailPrice) {
    return undefined
  }

  const priceAmount = parseFloat(retailPrice)
  return isNaN(priceAmount) ? undefined : priceAmount
}

function getStaticPrices(
  categoryType: string,
  staticPricingCategories: PricingCategory[] | undefined,
  locale: string,
  eventData?: EventDescription
): { price: string; originalPrice?: string } | undefined {
  const priceAmount = getStaticPriceAmount(
    categoryType,
    staticPricingCategories
  )
  if (priceAmount === undefined) {
    return undefined
  }

  const formattedLocale = convertLocaleIntoBCP47(locale)
  const promotion = eventData?.activePromotion?.data
  const discountedAmount = calculatePromotionDiscount(priceAmount, promotion)

  const originalFormatted = formatMoney(priceAmount, formattedLocale, 'EUR', 2)

  if (discountedAmount !== undefined) {
    return {
      price: formatMoney(discountedAmount, formattedLocale, 'EUR', 2),
      originalPrice: originalFormatted,
    }
  }

  return { price: originalFormatted }
}

function sortCategoriesByType(
  categories: PricingCategory[]
): PricingCategory[] {
  const order: Record<string, number> = {
    ADULT: 0,
    CHILD: 1,
    YOUTH: 2,
    SENIOR: 3,
    STUDENT: 4,
  }
  return [...categories].sort((a, b) => {
    const aOrder = order[a.ticket_category] ?? 99
    const bOrder = order[b.ticket_category] ?? 99
    return aOrder - bOrder
  })
}

function buildDisplayLabel(
  categoryLabel: string,
  ageRange: string,
  isYoungestCategory: boolean,
  t: (key: string) => string
): string {
  // Youngest category omits "Alter:" prefix as the age range already includes context
  const ageLabel = isYoungestCategory
    ? ageRange
    : `${t('product.show.checkout.age')}: ${ageRange}`
  return `${categoryLabel} (${ageLabel})`
}

function calculateAmountOffPercentage(
  originalPrice: Price | undefined,
  price: Price | undefined
): number {
  if (!originalPrice || !price) {
    return 0
  }

  const originalAmount = parseFloat(originalPrice.amount)
  const currentAmount = parseFloat(price.amount)

  // Handle invalid or zero original price
  if (isNaN(originalAmount) || originalAmount === 0) {
    return 0
  }

  // Handle invalid current amount
  if (isNaN(currentAmount)) {
    return 0
  }

  // Calculate percentage discount
  const discountPercentage =
    ((originalAmount - currentAmount) / originalAmount) * 100

  // Round and ensure non-negative (discount can't be negative)
  return Math.max(0, Math.round(discountPercentage))
}

/**
 * Formats categories for display by combining PricingCategory data with dynamic prices.
 *
 * PricingCategory is the source of truth because:
 * - It contains canonical age ranges (min_age, max_age) from EventDescription
 * - It defines all available categories with their metadata
 * - Prices are dynamic and come separately based on current selection/quantities
 *
 * We iterate over categories and look up prices, not the other way around,
 * to ensure all categories are shown even if price breakdown is incomplete.
 */
function formatCategories(
  categories: PricingCategory[],
  t: (key: string, params?: Record<string, any>) => string,
  locale: string,
  options?: FormatCategoriesOptions
): CategoryItem[] {
  const sortedCategories = options?.sortCategories
    ? sortCategoriesByType(categories)
    : categories

  return sortedCategories.map((category) => {
    const categoryLabel = t(
      `product.show.checkout.category.${category.ticket_category}`
    )
    // Age range comes from PricingCategory (canonical data)
    const ageRange = buildAgeRangeLabel(category.min_age, category.max_age, t)
    // Price is looked up from dynamic price breakdown (may be missing)
    const price = findPriceForCategory(
      category.ticket_category,
      options?.pricesByCategory
    )

    const originalPrice = findOriginalPriceForCategory(
      category.ticket_category,
      options?.pricesByCategory
    )
    const priceFormatted = price
      ? formatMoney(
          Number(price.amount) / 100,
          convertLocaleIntoBCP47(locale),
          'EUR',
          2
        )
      : undefined

    // Get the quantity for this category
    const categoryQuantity =
      options?.categoryQuantities?.[category.ticket_category] ?? 0

    // Only use price.formatted if it's a non-empty string
    // This prevents using cached prices with empty/undefined formatted values
    const hasValidPrice = priceFormatted && priceFormatted.trim() !== ''

    const isLoading = options?.loadingPricings === true

    // Always get static prices to use for original price (static, not dynamic)
    const staticPrices = getStaticPrices(
      category.ticket_category,
      options?.staticPricingCategories,
      locale,
      options?.eventData
    )

    // Determine display price
    const displayPrice =
      isLoading && categoryQuantity > 0
        ? undefined // Show skeleton when loading
        : hasValidPrice
          ? priceFormatted
          : staticPrices?.price

    // Determine display original price - always use static price, not dynamic
    // Static price already handles promotions and only shows originalPrice when there's a discount
    const displayOriginalPrice = staticPrices?.originalPrice

    const amountOffPercentage =
      originalPrice &&
      price &&
      calculateAmountOffPercentage(originalPrice, price) > 0
        ? calculateAmountOffPercentage(originalPrice, price)
        : undefined

    return {
      id: category.id,
      ticket_category: category.ticket_category,
      displayLabel: buildDisplayLabel(
        categoryLabel,
        ageRange,
        category.min_age === 0,
        t
      ),
      displayPrice,
      displayOriginalPrice,
      amountOffPercentage,
    }
  })
}

export function useFormattedPricingCategories() {
  const { t, locale } = useI18n()

  return {
    formatCategories: (
      categories: PricingCategory[],
      options?: FormatCategoriesOptions
    ) => formatCategories(categories, t, locale.value, options),
  }
}
